// START IMPORTS
import {
  createAppConfig,
  AppRequests,
  AppConfig,
  AppConstants,
} from '@ltvco/refresh-lib/ctx';
import { styled } from '@ltvco/refresh-lib/theme';
import { AlertProvider } from '@ltvco/refresh-lib/v1';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { ExitToApp } from '@mui/icons-material';
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Link,
} from 'react-router-dom';

import { constants, monitoringHubTestSideMenuItems } from 'appConstants';
import { appRequests } from 'appRequests';

import type { ResponseError } from '@ltvco/refresh-lib/utils';
import type { SideMenuItem } from '@ltvco/refresh-lib/v2';
// END IMPORTS

// START STYLED_COMPONENTS
const MenuItem = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.fontSize * 1.125,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  svg: {
    marginRight: theme.spacing(1),
  },
}));
// END STYLED_COMPONENTS

export function LibContextWrapper({
  children,
  trackEventGA4,
  trackEvent,
  logError,
  handleLogoutClick,
}: {
  children: React.ReactNode | React.ReactNode[];
  trackEventGA4: (eventToTrack: object, eventName?: string) => void;
  trackEvent: (category: string, action: string, label?: string) => void;
  logError: (
    context: string,
    error: Error | ResponseError,
    url: string | undefined
  ) => void;
  handleLogoutClick: () => void;
}) {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const navigate = useNavigate();
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const isInMonitoringHubTest = useFeatureIsOn('RFRSH-4311');
  const defaultConfig = createAppConfig();
  const sideMenuItems = isInMonitoringHubTest
    ? monitoringHubTestSideMenuItems
    : constants.config.sideMenuItems;

  function flattenMenuItems(menuItems: SideMenuItem[]): SideMenuItem[] {
    return menuItems.flatMap(({ childItems, ...item }) => [
      item,
      ...(childItems?.length ? flattenMenuItems(childItems) : []),
    ]);
  }

  const flatMenu = flattenMenuItems(sideMenuItems);
  const menuItems = flatMenu.map((item) => {
    return (
      <MenuItem to={item.route} data-cy={`menu_${item['data-cy']}`}>
        {item.icon} {item.text}
      </MenuItem>
    );
  });
  // END DERIVED_DATA_AND_UTILS

  // START DATA_PREPARATION
  const appConfig = {
    ...defaultConfig,
    routingUtils: {
      ...defaultConfig.routingUtils,
      navigate: navigate,
      location: location,
      useSearchParams,
    },
    logError: logError,
    trackEvent: trackEvent,
    trackEventGA4: trackEventGA4,
  };

  const appConstants = {
    ...constants,
    config: {
      ...constants.config,
      menuItems: [
        ...menuItems,
        <MenuItem
          to="#"
          onClick={() => handleLogoutClick()}
          data-cy="menu_log_out_btn"
        >
          <ExitToApp sx={{ color: 'rgba(0, 0, 0, 0.87)' }} /> Log Out
        </MenuItem>,
      ],
      sideMenuItems: [
        ...sideMenuItems,
        {
          route: '',
          icon: '',
          text: 'Logout',
          name: 'logout',
          state: '',
          'data-cy': 'menu_log_out_btn',
          action: handleLogoutClick,
        },
      ],
    },
  };
  // END DATA_PREPARATION

  // START RENDER
  return (
    <AlertProvider maxSnack={3} preventDuplicate autoHideDuration={5000}>
      <AppRequests.Provider value={appRequests}>
        <AppConfig.Provider value={appConfig as any}>
          <AppConstants.Provider value={appConstants}>
            {children}
          </AppConstants.Provider>
        </AppConfig.Provider>
      </AppRequests.Provider>
    </AlertProvider>
  );
  // END RENDER
}
